import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ShoppingBag, Gem, Sparkles, Crown, Zap, Gift, Star, Ban } from 'lucide-react';
import { cn } from '../../lib/utils';
import { purchaseDiamonds, purchaseRemoveAds } from '../../lib/stripe';
import { useAppContext } from '../../contexts/AppContext';
type ShopTab = 'diamonds' | 'themes' | 'powerups';
const DIAMOND_PACKAGES = [{
  id: 1,
  amount: 100,
  price: 0.99,
  bonus: 0,
  popular: false
}, {
  id: 2,
  amount: 500,
  price: 4.99,
  bonus: 50,
  popular: true
}, {
  id: 3,
  amount: 1200,
  price: 9.99,
  bonus: 200,
  popular: false
}, {
  id: 4,
  amount: 2500,
  price: 19.99,
  bonus: 500,
  popular: false
}, {
  id: 5,
  amount: 6500,
  price: 49.99,
  bonus: 1500,
  popular: false
}];
const THEMES = [{
  id: 1,
  name: 'Ocean Breeze',
  price: 500,
  colors: ['from-cyan-400', 'to-blue-600']
}, {
  id: 2,
  name: 'Sunset Glow',
  price: 500,
  colors: ['from-orange-400', 'to-pink-600']
}, {
  id: 3,
  name: 'Forest Green',
  price: 500,
  colors: ['from-emerald-400', 'to-green-600']
}, {
  id: 4,
  name: 'Royal Purple',
  price: 800,
  colors: ['from-purple-400', 'to-violet-600']
}, {
  id: 5,
  name: 'Golden Hour',
  price: 1000,
  colors: ['from-amber-400', 'to-yellow-600']
}];
const POWERUPS = [{
  id: 1,
  name: 'Extra Hints',
  description: 'Get 10 additional hints',
  price: 200,
  icon: '💡'
}, {
  id: 2,
  name: 'Time Freeze',
  description: 'Pause timer for 30 seconds',
  price: 150,
  icon: '⏸️'
}, {
  id: 3,
  name: 'Undo Move',
  description: 'Undo your last move',
  price: 100,
  icon: '↩️'
}, {
  id: 4,
  name: 'Double XP',
  description: '2x XP for next 5 games',
  price: 300,
  icon: '⚡'
}, {
  id: 5,
  name: 'Remove Ads',
  description: 'Remove all ads permanently',
  price: 100,
  icon: '🚫',
  special: true
}];
export function ShopScreen() {
  const [activeTab, setActiveTab] = useState<ShopTab>('diamonds');
  const {
    user,
    addDiamonds,
    spendDiamonds,
    updateUser
  } = useAppContext();
  const [purchasing, setPurchasing] = useState(false);
  const userDiamonds = user?.diamonds || 0;
  const ownedThemes = user?.ownedThemes || [];
  const ownedPowerups = user?.ownedPowerups || [];
  const hasRemoveAds = user?.hasRemoveAds || false;
  const handlePurchaseDiamonds = async (pkg: (typeof DIAMOND_PACKAGES)[0]) => {
    if (!user) {
      alert('Please sign in to purchase diamonds');
      return;
    }
    setPurchasing(true);
    const success = await purchaseDiamonds(pkg.id, pkg.amount + pkg.bonus, pkg.price);
    setPurchasing(false);
    if (success) {
      addDiamonds(pkg.amount + pkg.bonus);
      alert(`Successfully purchased ${pkg.amount + pkg.bonus} diamonds!`);
    } else {
      alert('Purchase failed. Please try again.');
    }
  };
  const handlePurchaseTheme = (theme: (typeof THEMES)[0]) => {
    if (!user) {
      alert('Please sign in to purchase themes');
      return;
    }
    if (ownedThemes.includes(theme.id.toString())) {
      alert('You already own this theme!');
      return;
    }
    if (spendDiamonds(theme.price)) {
      updateUser({
        ownedThemes: [...ownedThemes, theme.id.toString()]
      });
      alert(`${theme.name} theme purchased!`);
    } else {
      alert('Not enough diamonds!');
    }
  };
  const handlePurchasePowerup = (powerup: (typeof POWERUPS)[0]) => {
    if (!user) {
      alert('Please sign in to purchase power-ups');
      return;
    }
    if (powerup.id === 5) {
      // Remove Ads - special handling
      if (hasRemoveAds) {
        alert('You already have ad-free experience!');
        return;
      }
      if (spendDiamonds(powerup.price)) {
        updateUser({
          hasRemoveAds: true
        });
        alert('Ads removed! Enjoy ad-free gaming!');
      } else {
        alert('Not enough diamonds!');
      }
    } else {
      if (spendDiamonds(powerup.price)) {
        updateUser({
          ownedPowerups: [...ownedPowerups, powerup.id.toString()]
        });
        alert(`${powerup.name} purchased!`);
      } else {
        alert('Not enough diamonds!');
      }
    }
  };
  const handlePurchaseRemoveAdsDirect = async () => {
    if (!user) {
      alert('Please sign in to purchase');
      return;
    }
    if (hasRemoveAds) {
      alert('You already have ad-free experience!');
      return;
    }
    setPurchasing(true);
    const success = await purchaseRemoveAds('money');
    setPurchasing(false);
    if (success) {
      updateUser({
        hasRemoveAds: true
      });
      alert('Ads removed! Enjoy ad-free gaming!');
    } else {
      alert('Purchase failed. Please try again.');
    }
  };
  return <div className="flex flex-col h-full bg-slate-50" data-id="element-518">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white p-6 pb-8" data-id="element-519">
        <div className="flex items-center justify-center gap-3 mb-4" data-id="element-520">
          <ShoppingBag size={32} data-id="element-521" />
          <h1 className="text-3xl font-black" data-id="element-522">Shop</h1>
        </div>
        <div className="flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 w-fit mx-auto" data-id="element-523">
          <Gem className="text-blue-200" size={20} data-id="element-524" />
          <span className="font-bold text-lg" data-id="element-525">{userDiamonds}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-4 -mt-4 rounded-t-3xl" data-id="element-526">
        <div className="flex gap-2 pt-4" data-id="element-527">
          {[{
          id: 'diamonds' as ShopTab,
          label: '💎 Diamonds'
        }, {
          id: 'themes' as ShopTab,
          label: '🎨 Themes'
        }, {
          id: 'powerups' as ShopTab,
          label: '⚡ Power-ups'
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn('flex-1 py-3 px-4 rounded-t-xl font-semibold text-sm transition-colors', activeTab === tab.id ? 'bg-amber-50 text-amber-600 border-b-2 border-amber-600' : 'text-slate-500 hover:text-slate-700')} data-id="element-528">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4" data-id="element-529">
        {activeTab === 'diamonds' && <div className="space-y-3" data-id="element-530">
            {DIAMOND_PACKAGES.map(pkg => <Card key={pkg.id} className={cn('relative', pkg.popular && 'ring-2 ring-amber-500')} data-id="element-531">
                {pkg.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2" data-id="element-532">
                    <Badge variant="warning" className="bg-amber-500 text-white shadow-lg" data-id="element-533">
                      <Star size={12} className="mr-1" data-id="element-534" />
                      Most Popular
                    </Badge>
                  </div>}
                <div className="flex items-center justify-between" data-id="element-535">
                  <div className="flex items-center gap-4" data-id="element-536">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center" data-id="element-537">
                      <Gem className="text-white" size={32} data-id="element-538" />
                    </div>
                    <div data-id="element-539">
                      <div className="flex items-center gap-2 mb-1" data-id="element-540">
                        <h3 className="text-2xl font-black text-slate-900" data-id="element-541">
                          {pkg.amount}
                        </h3>
                        {pkg.bonus > 0 && <Badge variant="success" className="text-xs" data-id="element-542">
                            +{pkg.bonus} Bonus
                          </Badge>}
                      </div>
                      <p className="text-sm text-slate-500" data-id="element-543">Diamonds</p>
                    </div>
                  </div>
                  <Button variant={pkg.popular ? 'primary' : 'outline'} size="lg" onClick={() => handlePurchaseDiamonds(pkg)} disabled={purchasing} data-id="element-544">
                    ${pkg.price}
                  </Button>
                </div>
              </Card>)}
          </div>}

        {activeTab === 'themes' && <div className="grid grid-cols-2 gap-3" data-id="element-545">
            {THEMES.map(theme => {
          const owned = ownedThemes.includes(theme.id.toString());
          return <Card key={theme.id} className="p-0 overflow-hidden" data-id="element-546">
                  <div className={cn('h-24 bg-gradient-to-br', ...theme.colors)} data-id="element-547" />
                  <div className="p-3" data-id="element-548">
                    <h3 className="font-bold text-slate-900 mb-1" data-id="element-549">
                      {theme.name}
                    </h3>
                    {owned ? <Badge variant="success" className="text-xs" data-id="element-550">
                        Owned
                      </Badge> : <div className="flex items-center justify-between" data-id="element-551">
                        <div className="flex items-center gap-1 text-sm font-semibold text-slate-700" data-id="element-552">
                          <Gem size={14} className="text-blue-500" data-id="element-553" />
                          {theme.price}
                        </div>
                        <Button size="sm" variant="outline" onClick={() => handlePurchaseTheme(theme)} data-id="element-554">
                          Buy
                        </Button>
                      </div>}
                  </div>
                </Card>;
        })}
          </div>}

        {activeTab === 'powerups' && <div className="space-y-3" data-id="element-555">
            {POWERUPS.map(powerup => {
          const owned = powerup.id === 5 ? hasRemoveAds : ownedPowerups.includes(powerup.id.toString());
          const isRemoveAds = powerup.id === 5;
          return <Card key={powerup.id} className={cn(isRemoveAds && 'ring-2 ring-amber-500')} data-id="element-556">
                  <div className="flex items-center gap-4" data-id="element-557">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-2xl" data-id="element-558">
                      {powerup.icon}
                    </div>
                    <div className="flex-1" data-id="element-559">
                      <h3 className="font-bold text-slate-900 mb-1" data-id="element-560">
                        {powerup.name}
                        {isRemoveAds && <Badge variant="warning" className="ml-2 text-xs" data-id="element-561">
                            Special
                          </Badge>}
                      </h3>
                      <p className="text-xs text-slate-500" data-id="element-562">
                        {powerup.description}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-563">
                      {owned ? <Badge variant="success" data-id="element-564">Owned</Badge> : <>
                          <div className="flex items-center gap-1 text-sm font-semibold text-slate-700 mb-2" data-id="element-565">
                            <Gem size={14} className="text-blue-500" data-id="element-566" />
                            {powerup.price}
                          </div>
                          <div className="space-y-1" data-id="element-567">
                            <Button size="sm" onClick={() => handlePurchasePowerup(powerup)} data-id="element-568">
                              Buy
                            </Button>
                            {isRemoveAds && <Button size="sm" variant="outline" onClick={handlePurchaseRemoveAdsDirect} disabled={purchasing} className="text-xs" data-id="element-569">
                                or $0.99
                              </Button>}
                          </div>
                        </>}
                    </div>
                  </div>
                </Card>;
        })}
          </div>}
      </div>
    </div>;
}